import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { idle } from '@freelancer/operators';
import { leaveZone } from '@freelancer/time-utils';
import { intervalBackoff } from 'backoff-rxjs';
import * as Rx from 'rxjs';
import { audit, filter, map, tap } from 'rxjs/operators';
import { Tracking } from './tracking.service';

@Injectable({ providedIn: 'root' })
export class MemoryTracking {
  constructor(
    private ngZone: NgZone,
    private router: Router,
    private tracking: Tracking,
  ) {}

  /**
   * Send events for current memory usage.
   *
   * Events are sent either:
   *  - On navigation end.
   *  - On a heartbeat (starting at 10 second and doubling up to 10 minutes).
   *
   * They are send only when the browser is idle.
   */
  monitor(): Rx.Observable<unknown> {
    const initialTime = Date.now();

    return Rx.merge(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(event => ({ type: 'navigationEnd' as const, event })),
      ),

      intervalBackoff(
        {
          initialInterval: 10_000, // 10 seconds
          maxInterval: 10_000 * 60, // 10 minutes
        },
        leaveZone(this.ngZone, Rx.asyncScheduler),
      ).pipe(map(() => ({ type: 'heartbeat' as const }))),
    ).pipe(
      /** Wait for browser to be idle and drop any extra events if it's not */
      audit(() => idle(this.ngZone)),

      tap(event => {
        if (!window.performance?.memory) {
          return;
        }

        this.tracking.trackMemoryProfiling({
          reason: event.type,
          timeSinceWebappLoaded: Date.now() - initialTime,
          jsHeapSizeLimit: window.performance.memory.jsHeapSizeLimit,
          totalJSHeapSize: window.performance.memory.totalJSHeapSize,
          usedJSHeapSize: window.performance.memory.usedJSHeapSize,
        });
      }),
    );
  }
}
